import { Logger } from '@frontend/logger';
import { PackageClient, PackageWorkflowClient } from '@frontend/package/api';
import { Package, PackageStateName, PackageTriggerName } from '@frontend/package/types';

const PACKAGE_PROCESSING_POLL_ATTEMPTS = 30;
const PACKAGE_PROCESSING_POLL_INTERVAL_MS = 500;

const OPENABLE_DROP_PACKAGE_STATES = new Set([PackageStateName.PACKAGE_DROP_OFF_PENDING, PackageStateName.PACKAGE_DROP_OFF_STARTED]);

const wait = (duration: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, duration));

/**
 * Poll until a newly created drop-off package reaches a state where PACKAGE_OPEN_SLOT is valid.
 * Returns null if the package leaves PACKAGE_CREATED without becoming openable, or on timeout.
 */
export const waitForOpenableDropPackage = async (
    accountId: string,
    transactionId: string,
    packageId: string,
    options?: { attempts?: number; intervalMs?: number }
): Promise<Package | null> => {
    const attempts = options?.attempts ?? PACKAGE_PROCESSING_POLL_ATTEMPTS;
    const intervalMs = options?.intervalMs ?? PACKAGE_PROCESSING_POLL_INTERVAL_MS;

    for (let attempt = 0; attempt < attempts; attempt += 1) {
        const dropPackage = await PackageClient.fetchPackage(accountId, transactionId, packageId);
        if (OPENABLE_DROP_PACKAGE_STATES.has(dropPackage.state)) return dropPackage;
        if (dropPackage.state !== PackageStateName.PACKAGE_CREATED) return null;

        await wait(intervalMs);
    }

    return null;
};

/**
 * Wait for each drop package to become openable. Unready packages are skipped (logged), not thrown.
 */
export const waitForOpenableDropPackages = async (
    accountId: string,
    packages: Package[],
    options?: { attempts?: number; intervalMs?: number }
): Promise<Package[]> => {
    const openable: Package[] = [];

    for (const pkg of packages) {
        if (!pkg.id || !pkg.transaction_id) {
            Logger.warn('Bulk move drop package missing id or transaction_id', { packageId: pkg.id });
            continue;
        }

        const ready = await waitForOpenableDropPackage(accountId, pkg.transaction_id, pkg.id, options);
        if (!ready) {
            Logger.warn('Bulk move drop package was not ready to open', {
                transactionId: pkg.transaction_id,
                packageId: pkg.id
            });
            continue;
        }
        openable.push(ready);
    }

    return openable;
};

/** Fire PACKAGE_OPEN_SLOT for every openable drop package still in drop-off pending. */
export const triggerOpenSlotForDropPackages = async (
    accountId: string,
    packages: Package[],
    options?: { location?: string; actor?: string }
): Promise<void> => {
    for (const pkg of packages) {
        if (pkg.state !== PackageStateName.PACKAGE_DROP_OFF_PENDING) continue;
        if (!pkg.id || !pkg.transaction_id) continue;

        try {
            await PackageWorkflowClient.triggerPackageState(accountId, pkg.transaction_id, pkg.id, {
                trigger: PackageTriggerName.PACKAGE_OPEN_SLOT,
                location: options?.location,
                actor: options?.actor
            });
        } catch (err) {
            Logger.error('Failed to start destination drop-off open', err, {
                transactionId: pkg.transaction_id,
                packageId: pkg.id
            });
        }
    }
};

const OPENABLE_REMOVAL_PACKAGE_STATES = new Set([PackageStateName.PACKAGE_REMOVAL_PENDING, PackageStateName.PACKAGE_REMOVAL_STARTED]);

/**
 * Poll until a newly created removal package reaches a state where PACKAGE_OPEN_SLOT is valid.
 * Returns null if the package leaves PACKAGE_CREATED without becoming openable, or on timeout.
 */
export const waitForOpenableRemovalPackage = async (
    accountId: string,
    transactionId: string,
    packageId: string,
    options?: { attempts?: number; intervalMs?: number }
): Promise<Package | null> => {
    const attempts = options?.attempts ?? PACKAGE_PROCESSING_POLL_ATTEMPTS;
    const intervalMs = options?.intervalMs ?? PACKAGE_PROCESSING_POLL_INTERVAL_MS;

    for (let attempt = 0; attempt < attempts; attempt += 1) {
        const removalPackage = await PackageClient.fetchPackage(accountId, transactionId, packageId);
        if (OPENABLE_REMOVAL_PACKAGE_STATES.has(removalPackage.state)) return removalPackage;
        if (removalPackage.state !== PackageStateName.PACKAGE_CREATED) return null;

        await wait(intervalMs);
    }

    return null;
};

/** Wait for each removal package to become openable. Unready packages are skipped (logged), not thrown. */
export const waitForOpenableRemovalPackages = async (
    accountId: string,
    packages: Package[],
    options?: { attempts?: number; intervalMs?: number }
): Promise<Package[]> => {
    const openable: Package[] = [];

    for (const pkg of packages) {
        if (!pkg.id || !pkg.transaction_id) {
            Logger.warn('Bulk removal package missing id or transaction_id', { packageId: pkg.id });
            continue;
        }

        const ready = await waitForOpenableRemovalPackage(accountId, pkg.transaction_id, pkg.id, options);
        if (!ready) {
            Logger.warn('Bulk removal package was not ready to open', {
                transactionId: pkg.transaction_id,
                packageId: pkg.id
            });
            continue;
        }
        openable.push(ready);
    }

    return openable;
};

/** Fire PACKAGE_OPEN_SLOT for every openable removal package still in removal pending. */
export const triggerOpenSlotForRemovalPackages = async (
    accountId: string,
    packages: Package[],
    options?: { location?: string; actor?: string }
): Promise<void> => {
    for (const pkg of packages) {
        if (pkg.state !== PackageStateName.PACKAGE_REMOVAL_PENDING) continue;
        if (!pkg.id || !pkg.transaction_id) continue;

        try {
            await PackageWorkflowClient.triggerPackageState(accountId, pkg.transaction_id, pkg.id, {
                trigger: PackageTriggerName.PACKAGE_OPEN_SLOT,
                location: options?.location,
                actor: options?.actor
            });
        } catch (err) {
            Logger.error('Failed to start removal open', err, {
                transactionId: pkg.transaction_id,
                packageId: pkg.id
            });
        }
    }
};
